import React, { useState } from "react";
import styled from "styled-components";
import GlobalPopupContainerCenter from "../globalPopup/GlobalPopupContainerCenter";
import constructOptimisedImageUrl from "../../utils/imageOptimisation/constructOptimisedImageUrl";
import { TextTheme, ColorTheme, BorderRadiusTheme, TallTextTheme, SpacingTheme } from "../globaStyledComponents/globalStyledComponentsStyling";
import useClickOutside from "../../hook/useClickOutside";
import useIsDesktopSize from "../../hook/useIsDesktopSize";
import { SwiperSlide } from "swiper/react";
import ImageGallery from "../gallery/ImageGallery";
import ContinueBtn from "../btn/continueBtn/continueBtn";
import { getCategoryLabel } from "../../utils/extras/categoryLabels";

interface ExtraImage {
    key: string;
    alt: string;
    height: number | null;
    width: number | null;
}

interface Extra {
    id: string;
    draft: boolean;
    status: string;
    extraImages: ExtraImage[];
    name: string;
    description?: string;
    price: number;
    productType?: string;
    selectedLocations?: string[];
    userhostmodelID: string;
    productTitle?: string;
    productDescription?: string;
    netPrice?: number;
    productImages?: string[];
    productFulfillmentType?: string;
    pricingType?: string;
    inventory?: number;
    location?: string;
    fulfillmentRadius?: number;
    guestOnlyPurchase?: boolean;
    minOrderQuantity?: number;
    maxOrderQuantity?: number;
    minOrderAmount?: number;
    maxOrderAmount?: number;
    maxGuests?: number;
    duration?: number;
    minimumNotice?: number;
    unavailableDates?: string[];
    internalCode?: string;
    unlimitedInventory?: boolean;
    approved?: boolean;
    instantBookEnabled?: boolean;
    categories?: string[];
    tags?: string[];
    createdAt?: string;
    updatedAt?: string;
}

interface ExtraPopupProps {
    isOpen: boolean;
    onClose: () => void;
    extraData: Extra;
    onOrder?: () => void;
}

const PreviewContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px;
    width: 100%;
    margin: 0 auto;
    position: relative;
    height: 100%;
    overflow-y: auto;
    padding-top: 80px;

    @media (min-width: 768px) {
        flex-direction: row;
        gap: 32px;
        padding: 32px;
        height: auto;
        overflow-y: visible;
        padding-top: 32px;
    }
`;

const ImageSection = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const ExtraPopupImage = styled.img`
    width: 100%;
    max-width: 520px;
    height: 300px;
    object-fit: cover;
    border-radius: ${BorderRadiusTheme.radius_24.borderRadius};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

    @media (min-width: 768px) {
        width: 520px;
        height: 520px;
    }
`;

// Gallery styles unified via ImageGallery

const ExtraPopupImageWrapper = styled(SwiperSlide)`
    width: 100%;
    height: 100%;
`;

const ContentSection = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    position: relative;
`;

const ExtraPopupTitle = styled.h1`
    font-size: ${TextTheme.sans_800_semi.fontSize};
    line-height: ${TextTheme.sans_800_semi.lineHeight};
    font-family: ${TextTheme.sans_800_semi.fontFamily};
    color: ${ColorTheme.Black.color};
    margin: 0 0 8px 0;

    @media (min-width: 768px) {
        font-size: ${TextTheme.sans_900_semi.fontSize};
        line-height: ${TextTheme.sans_900_semi.lineHeight};
    }
`;

const ExtraPopupDescription = styled.p`
    font-size: ${TallTextTheme.sans_text_300.fontSize};
    font-family: ${TallTextTheme.sans_text_300.fontFamily};
    line-height: ${TallTextTheme.sans_text_300.lineHeight};
    color: ${ColorTheme.Black.color};
    margin: 0;
    max-width: 400px;

    @media (min-width: 768px) {
        max-height: 320px;
        overflow-y: auto;
        overflow-x: hidden;
        padding-right: 8px;

        /* Custom scrollbar styling */
        &::-webkit-scrollbar {
            width: 4px;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background: ${ColorTheme.Grey2.color};
            border-radius: 2px;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: ${ColorTheme.Grey.color};
        }
    }
`;

const ExtraPopupPrice = styled.div`
    font-size: ${TextTheme.sans_700_semi.fontSize};
    line-height: ${TextTheme.sans_700_semi.lineHeight};
    font-family: ${TextTheme.sans_700_semi.fontFamily};
    color: ${ColorTheme.Black.color};
    margin: 0;

    .total-text {
        font-size: ${TextTheme.sans_400.fontSize};
        font-family: ${TextTheme.sans_400.fontFamily};
        line-height: ${TextTheme.sans_400.lineHeight};
    }
`;

const ExtraPopupType = styled.div`
    font-size: ${TextTheme.sans_400.fontSize};
    font-family: ${TextTheme.sans_400.fontFamily};
    line-height: ${TextTheme.sans_400.lineHeight};
    color: ${ColorTheme.Grey.color};
    /* Render label as-is; mapping provides final casing */
    margin: 0 0 16px 0;
`;

const OrderButtonWrapper = styled.div`
    position: absolute;
    bottom: 0;
    right: 0;
    width: 120px;
    z-index: 10;

    @media (max-width: 767px) {
        display: none;
    }
`;

const PriceWrapper = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    height: 48px;

    @media (max-width: 767px) {
        display: none;
    }
`;

const NoImagePlaceholder = styled.div`
    width: 100%;
    max-width: 520px;
    height: 300px;
    background-color: ${ColorTheme.Grey2.color};
    border-radius: ${BorderRadiusTheme.radius_24.borderRadius};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${ColorTheme.Grey.color};
    font-size: ${TextTheme.sans_500.fontSize};
    font-family: ${TextTheme.sans_500.fontFamily};

    @media (min-width: 768px) {
        width: 520px;
        height: 520px;
    }
`;

const MobilePriceDisplay = styled.div`
    display: none;
    
    @media (max-width: 767px) {
        display: flex;
        align-items: center;
        font-size: ${TextTheme.sans_700_semi.fontSize};
        line-height: ${TextTheme.sans_700_semi.lineHeight};
        font-family: ${TextTheme.sans_700_semi.fontFamily};
        color: ${ColorTheme.Black.color};
    }
`;

const MobileButtonContainer = styled.div`
    display: none;
    
    @media (max-width: 767px) {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: ${SpacingTheme.spacing_300.padding} ${SpacingTheme.spacing_400.padding};
        margin-top: auto;
        position: sticky;
        bottom: 0;
        z-index: 1;
        background-color: transparent;
    }
`;

const QuantitySelector = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 16px 0;
`;

const QuantityLabel = styled.span`
    font-size: ${TextTheme.sans_400.fontSize};
    font-family: ${TextTheme.sans_400.fontFamily};
    line-height: ${TextTheme.sans_400.lineHeight};
    color: ${ColorTheme.Black.color};
`;

const QuantityControls = styled.div`
    display: flex;
    align-items: center;
`;

const QuantityButton = styled.button`
    width: 36px;
    height: 36px;
    border: 1px solid ${ColorTheme.Grey2.color};
    background: ${ColorTheme.White.color};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: ${TextTheme.sans_400.fontSize};
    font-family: ${TextTheme.sans_400.fontFamily};
    color: ${ColorTheme.Black.color};
    transition: all 0.2s ease;

    img {
        width: 16px;
        height: 16px;
    }

    &:hover {
        background: ${ColorTheme.Grey4.color};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const QuantityDisplay = styled.span`
    font-size: ${TextTheme.sans_400_semi.fontSize};
    font-family: ${TextTheme.sans_400_semi.fontFamily};
    line-height: ${TextTheme.sans_400_semi.lineHeight};
    color: ${ColorTheme.Black.color};
    min-width: 40px;
    text-align: center;
`;

export default function ExtraPopup({ isOpen, onClose, extraData, onOrder }: ExtraPopupProps) {
    const [quantity, setQuantity] = useState(1);
    const wrapperRef = useClickOutside(isOpen, () => {
        onClose();
    });
    const isDesktopSize = useIsDesktopSize();

    const getImageKey = (imgObj: any): string => {
        if (typeof imgObj === 'string') {
            try {
                const parsed = JSON.parse(imgObj);
                return parsed.key || '';
            } catch {
                return imgObj;
            }
        }
        return imgObj?.key || '';
    };

    const formatPrice = (price: number) => {
        const totalPrice = price * quantity;
        return `$${totalPrice.toFixed(2)} AUD <span class="total-text">total</span>`;
    };

    const formatProductType = (type: string) => {
        return type.replace(/_/g, ' ').toLowerCase();
    };

    const handleOrder = () => {
        if (onOrder) {
            onOrder();
        }
        onClose();
    };

    if (!isOpen) return null;

    return (
        <GlobalPopupContainerCenter
            ref={wrapperRef}
            closePopup={onClose}
            noContinueBtn={true}
            noCancelBtn={true}
            noBackBtn={isDesktopSize}
            desktopWidth="960px"
            height="calc(100vh - 12px)"
            desktopHeight="auto"
        >
            <PreviewContent>
                <ImageSection>
                    {extraData.extraImages && extraData.extraImages.length > 0 ? (
                        <ImageGallery images={extraData.extraImages.map(img => constructOptimisedImageUrl(getImageKey(img), 800))} altPrefix={extraData.name} />
                    ) : (
                        <NoImagePlaceholder>
                            No image available
                        </NoImagePlaceholder>
                    )}
                </ImageSection>
                <ContentSection>
                    <ExtraPopupTitle>{extraData.name}</ExtraPopupTitle>
                    {extraData.categories && extraData.categories.length > 0 && (
                        <ExtraPopupType>{getCategoryLabel(extraData.categories[0])}</ExtraPopupType>
                    )}
                    <QuantitySelector>
                        <QuantityControls>
                            <QuantityButton
                                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                disabled={quantity <= 1}
                            >
                                <img src="https://d292awxalydr86.cloudfront.net/Universal+icons/minus.svg" alt="minus" />
                            </QuantityButton>
                            <QuantityDisplay>{quantity}</QuantityDisplay>
                            <QuantityButton
                                onClick={() => setQuantity(quantity + 1)}
                                disabled={extraData.maxOrderQuantity ? quantity >= extraData.maxOrderQuantity : false}
                            >
                                <img src="https://d292awxalydr86.cloudfront.net/Universal+icons/plus.svg" alt="plus" />
                            </QuantityButton>
                        </QuantityControls>
                    </QuantitySelector>
                    {extraData.description && (
                        <ExtraPopupDescription>{extraData.description}</ExtraPopupDescription>
                    )}
                    <PriceWrapper>
                        <ExtraPopupPrice dangerouslySetInnerHTML={{ __html: formatPrice(extraData.price) }} />
                    </PriceWrapper>
                    <OrderButtonWrapper>
                        <ContinueBtn
                            isLoading={false}
                            disabled={true}
                            background="black"
                            round={true}
                            style={{ width: '100%', height: '48px' }}
                            onClick={handleOrder}
                        >
                            Order
                        </ContinueBtn>
                    </OrderButtonWrapper>
                </ContentSection>
            </PreviewContent>
            <MobileButtonContainer>
                <MobilePriceDisplay>
                    ${(extraData.price * quantity).toFixed(2)} AUD
                </MobilePriceDisplay>
                <ContinueBtn
                    isLoading={false}
                    disabled={true}
                    background="black"
                    round={true}
                    style={{ width: '120px', height: '48px' }}
                    onClick={handleOrder}
                >
                    Order
                </ContinueBtn>
            </MobileButtonContainer>
        </GlobalPopupContainerCenter>
    );
} 